import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useAuth } from '../../auth/AuthProvider';
import { htmlToPlain } from '../../lib/richText';
import { dateTime } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { useEmail, useEmailMessages, type EmailDocumentType } from './api';

/**
 * What was emailed about a document, or to a contact (all their documents): newest first, with
 * who sent it, the addresses, the subject and the files. Shown only with Email on.
 */
export function EmailHistory(
  props: (
    | { documentType: EmailDocumentType; documentId: string; contactId?: never }
    | { contactId: string; documentType?: never; documentId?: never }
  ) & { sx?: SxProps<Theme> },
) {
  const email = useEmail();
  const { can } = useAuth();
  const { format } = useShop();
  const { sx, ...filter } = props;
  const allowed = email.visible && (can('email.send') || can('email.manage'));
  const messages = useEmailMessages(filter, allowed);
  const rows = messages.data?.data ?? [];
  if (!allowed || rows.length === 0) return null;
  return (
    <Card sx={sx}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <MailOutlineRounded color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Emails
          </Typography>
        </Stack>
        <Stack spacing={1.5}>
          {rows.map((m) => (
            <Box
              key={m.id}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                pt: 1.5,
                '&:first-of-type': { borderTop: 0, pt: 0 },
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {m.recipientName ? `${m.recipientName} · ` : ''}
                  {m.to.join(', ')}
                </Typography>
                {m.status === 'FAILED' ? (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    icon={<ErrorOutlineRounded />}
                    label="Not sent"
                  />
                ) : (
                  <Chip size="small" color="success" variant="outlined" label="Sent" />
                )}
                {props.contactId && m.documentRef && (
                  <Chip size="small" variant="outlined" label={m.documentRef} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {dateTime(m.createdAt, format)} · {m.sentBy.name}
                </Typography>
              </Stack>
              {m.cc.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Cc {m.cc.join(', ')}
                </Typography>
              )}
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {m.subject}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {htmlToPlain(m.body)}
              </Typography>
              {m.fileNames.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                >
                  <AttachFileRounded sx={{ fontSize: 14 }} /> {m.fileNames.join(', ')}
                </Typography>
              )}
              {m.fromAddress && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  From {m.fromAddress}
                </Typography>
              )}
              {m.error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {m.error}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
